import { createSelector } from 'reselect';
import find from 'lodash.find';
import moment from 'moment-timezone';

import moneyFormatter from '../utils/formatter';
import { selectedLeaseOption } from './leaseFilter';
import {
  LEASE_FILTER, OFFERS, DATE_FORMAT, ALL_VEHICLES_FLAG,
  ALL_VEHICLES_FLAG_VALUE, SORT_OPTIONS, SORT_OPTION_TYPES, TYPE_FLAG,
} from './constants';
import { getSelectedVehicle } from './vehicles';
import { getOfferDetails } from './offerDetails';
import getTerm from '../helpers/offers';

import placeholder from '../../data/placeHolderCopy';

const specialTypes = window.offers && window.offers.specials
  ? window.offers.specials
  : placeholder.specials;

const labels = window.offers && window.offers.labels
  ? window.offers.labels
  : placeholder.labels;

const getStateOffers = (state) => state.offers;
const getStateDefaultSort = (state) => state.defaultSort;

const blades = (window.offers && window.offers.blades)
  ? window.offers.blades : placeholder.blades;

function generateOfferPayment(offer) {
  if (offer.LeasePaymentInfo) {
    return `${moneyFormatter(offer.LeasePaymentInfo.BaseMonthlyPayment,
      labels.offerDetails.dollarSign)}`;
  } if (offer.PaymentTerms) {
    return `${offer.PaymentTerms.FinanceTerms[0].APR}%`;
  }

  return offer.payment || '';
}

function filterByFlag(offers, flag, value, keep = true) {
  return offers.filter((offer) => {
    if (offer[flag] && offer[flag] === value) {
      return keep;
    }

    return !keep;
  });
}

function bestAPR(offer) {
  return offer.PaymentTerms.FinanceTerms.sort((first, second) => first.APR - second.APR)[0].APR;
}

function sortBestFinanceOffers(offers) {
  return offers.sort((first, second) => {
    const firstOfferBestApr = bestAPR(first);
    const secondOfferBestApr = bestAPR(second);

    if (firstOfferBestApr !== secondOfferBestApr) {
      return firstOfferBestApr - secondOfferBestApr;
    } if (first.BusinessPriority !== second.BusinessPriority) {
      return first.BusinessPriority - second.BusinessPriority;
    }

    return 0;
  });
}

function sortBestLeaseOffers(offers) {
  return offers.sort((first, second) => {
    if (first.LeasePaymentInfo.BaseMonthlyPayment
      !== second.LeasePaymentInfo.BaseMonthlyPayment) {
      return first.LeasePaymentInfo.BaseMonthlyPayment - second.LeasePaymentInfo.BaseMonthlyPayment;
    } if (first.BusinessPriority !== second.BusinessPriority) {
      return first.BusinessPriority - second.BusinessPriority;
    }

    return 0;
  });
}

function sortBestOffers(offers, type) {
  let sortedOffers;

  if (type === OFFERS.TYPES.FINANCE) {
    sortedOffers = sortBestFinanceOffers(offers);
  } else {
    sortedOffers = sortBestLeaseOffers(offers);
  }

  return sortedOffers;
}

function sortOffers(offers, defaultSort) {
  const offersByPriority = {};
  let sortedOffers = [];
  let bestOffers = [];
  let secondBestOffers = [];
  let restOfOffers = [];

  if (defaultSort && SORT_OPTIONS.indexOf(defaultSort.toLowerCase()) !== -1) {
    const sort = defaultSort.toLowerCase();

    bestOffers = filterByFlag(offers, TYPE_FLAG, SORT_OPTION_TYPES[sort]);

    if (bestOffers.length) {
      bestOffers = sortBestOffers(bestOffers, SORT_OPTION_TYPES[sort]);
      sortedOffers = sortedOffers.concat(bestOffers);
    }

    const sortType = SORT_OPTION_TYPES[sort] === OFFERS.TYPES.FINANCE
      ? OFFERS.TYPES.LEASE : OFFERS.TYPES.FINANCE;

    secondBestOffers = filterByFlag(offers, TYPE_FLAG, sortType);

    if (secondBestOffers.length) {
      secondBestOffers = sortBestOffers(secondBestOffers, sortType);
      sortedOffers = sortedOffers.concat(secondBestOffers);
    }

    restOfOffers = offers.filter((offer) => {
      if (offer[TYPE_FLAG] !== OFFERS.TYPES.FINANCE && offer[TYPE_FLAG] !== OFFERS.TYPES.LEASE) {
        return true;
      }

      return false;
    });
  } else {
    restOfOffers = offers;
  }

  restOfOffers.forEach((offer) => {
    let key = offer.BusinessPriority;
    const numericPriority = parseInt(offer.BusinessPriority, 10);

    if (!offer.BusinessPriority
      || !(numericPriority >= 1 && numericPriority <= 10)) {
      key = Infinity;
    }

    if (!offersByPriority[key]) {
      offersByPriority[key] = [offer];
    } else {
      offersByPriority[key].push(offer);
    }
  });

  // Order by priority
  const priorities = Object.keys(offersByPriority).sort((first, second) => parseInt(first, 10) - parseInt(second, 10));

  priorities.forEach((priority) => {
    sortedOffers = sortedOffers.concat(offersByPriority[priority]);
  });

  return sortedOffers;
}

function sortOfferGroups(offerGroups, defaultSort) {
  Object.keys(offerGroups).forEach((group) => {
    offerGroups[group] = sortOffers(offerGroups[group], defaultSort);
  });

  return offerGroups;
}

const getOffers = createSelector(
  [getStateOffers], (offers) => {
    // Compute values that are common to all offers independent of type
    // e.g formatted expiration date
    let computedOffers = [];

    if (offers.length > 0) {
      computedOffers = offers.map((offer) => {
        if (offer.EndDate) {
          offer.expirationDate = moment(offer.EndDate)
            .format(DATE_FORMAT.EXPIRATION);
        }

        return offer;
      });
    }

    return computedOffers;
  },
);

export const featured = createSelector(
  [getOffers, getSelectedVehicle, getStateDefaultSort], (offers, selectedVehicle, defaultSort) => {
    let selectedOffers = {};
    const nonFeaturedSelectedOffers = {};
    // let allOffers = []; // offers of type "all vehicles"

    blades.forEach((blade) => {
      selectedOffers[blade.offersModelGroupName] = [];
      nonFeaturedSelectedOffers[blade.offersModelGroupName] = [];
    });

    if (offers.length) {
      offers = filterByFlag(offers, ALL_VEHICLES_FLAG, ALL_VEHICLES_FLAG_VALUE, false);

      if (selectedVehicle.vehicle === undefined) {
        offers.forEach((offer) => {
          offer.payment = generateOfferPayment(offer);

          offer.term = getTerm(offer);

          if (offer.IsFeatured && offer.SalesProgramType !== OFFERS.TYPES.HCPV) {
            offer.type = 'featured';

            if ((selectedOffers[offer.ModelGroupName]
                && selectedOffers[offer.ModelGroupName].length === 0)
                || (selectedOffers[offer.ModelGroupName]
                && !selectedOffers[offer.ModelGroupName].IsFeatured)) {
              selectedOffers[offer.ModelGroupName].push(offer);
            }
          } else if (offer.SalesProgramType !== OFFERS.TYPES.HCPV) {
            nonFeaturedSelectedOffers[offer.ModelGroupName].push(offer);
          }
        });
      } else {
        selectedOffers[selectedVehicle.vehicle.model] = offers.filter((offer) => offer.IsFeatured
          && offer.ModelGroupName === selectedVehicle.vehicle.model
          && offer.SalesProgramType !== OFFERS.TYPES.HCPV).map((el) => {
          el.payment = generateOfferPayment(el);

          el.term = getTerm(el);

          return el;
        });

        nonFeaturedSelectedOffers[selectedVehicle.vehicle.model] = offers.filter((offer) => !offer.IsFeatured
          && offer.ModelGroupName === selectedVehicle.vehicle.model).map((el) => {
          el.payment = generateOfferPayment(el);

          el.term = getTerm(el);

          return el;
        });
      }
    }

    Object.getOwnPropertyNames(selectedOffers).forEach((property) => {
      if (selectedOffers[property].length === 0
        && nonFeaturedSelectedOffers[property].length) {
        if (selectedVehicle.vehicle === undefined) {
          selectedOffers[property] = [sortOffers(nonFeaturedSelectedOffers[property])[0]];
        } else {
          selectedOffers[property] = sortOffers(nonFeaturedSelectedOffers[property], defaultSort);
        }

        // There are no featured offers, so we tag the first one as the "featured"
        selectedOffers[property][0].type = 'featured';
      } else if (selectedVehicle.vehicle === undefined) {
        // TODO: Check order
        selectedOffers = sortOfferGroups(selectedOffers, defaultSort);
      } else {
        selectedOffers = sortOfferGroups(selectedOffers, defaultSort);
      }

      if (selectedOffers[property].length === 0) {
        delete selectedOffers[property];
      }
    });

    return { offersByModel: selectedOffers };
  },
);

function getOffersByType(offers, type, selectedVehicle, defaultSort) {
  const selectedOffers = {};

  blades.forEach((blade) => {
    selectedOffers[blade.offersModelGroupName] = [];
  });

  if (offers.length) {
    offers.filter((el) => el.SalesProgramType === type)
      .forEach((el) => {
        if (selectedVehicle.vehicle === undefined
            || el.ModelGroupName === selectedVehicle.vehicle.model) {
          switch (type) {
            case OFFERS.TYPES.LEASE:
              el.payment = `${moneyFormatter(el.LeasePaymentInfo.BaseMonthlyPayment,
                labels.offerDetails.dollarSign)}`;
              break;
            case OFFERS.TYPES.FINANCE:
              if (el.PaymentTerms) {
                el.payment = `${el.PaymentTerms.FinanceTerms[0].APR}%`;
              } else {
                el.payment = '';
              }
              break;
            case OFFERS.TYPES.HCPV:
              if (el.PaymentTerms) { // Finance
                el.payment = `${el.PaymentTerms.FinanceTerms[0].APR}%`;
              } else if (el.LeasePaymentInfo) { // Lease
                el.payment = `${moneyFormatter(el.LeasePaymentInfo.BaseMonthlyPayment,
                  labels.offerDetails.dollarSign)}`;
              } else if (el.OfferAmount) { // Special programs
                el.payment = `${moneyFormatter(el.OfferAmount, labels.offerDetails.dollarSign)}`;
              } else {
                el.payment = '';
              }
              break;
            default:
              if (specialTypes.indexOf(type) !== -1) {
                el.payment = `${moneyFormatter(el.OfferAmount, labels.offerDetails.dollarSign)}`;
              } else {
                el.payment = '';
              }
              break;
          }

          el.term = getTerm(el);

          if (selectedOffers[el.ModelGroupName]) {
            selectedOffers[el.ModelGroupName].push(el);
          }
        }
      });
  }

  Object.getOwnPropertyNames(selectedOffers).forEach((property) => {
    if (selectedOffers[property].length === 0) {
      delete selectedOffers[property];
    }
  });

  return sortOfferGroups(selectedOffers, defaultSort);
}

export const unfilteredLeases = createSelector(
  [getOffers, getSelectedVehicle, getStateDefaultSort],
  (offers, selectedVehicle, defaultSort) => {
    if (selectedVehicle.vehicle === undefined) {
      return getOffersByType(offers, OFFERS.TYPES.LEASE, selectedVehicle);
    }

    return getOffersByType(offers, OFFERS.TYPES.LEASE, selectedVehicle, defaultSort);
  },
);

export const lease = createSelector(
  [unfilteredLeases, selectedLeaseOption], (offers, leaseFilter) => {
    const leaseOffers = {};

    if (Object.keys(offers).length > 0) {
      Object.keys(offers).forEach((model) => {
        leaseOffers[model] = offers[model].filter((offer) => {
          const payment = parseFloat(offer.LeasePaymentInfo.BaseMonthlyPayment);

          if (!leaseFilter) {
            return true;
          }

          switch (leaseFilter.type) {
            case LEASE_FILTER.TYPES.LESS: {
              if (payment < leaseFilter.filtermaximumnumber) {
                return true;
              }

              return false;
            }

            case LEASE_FILTER.TYPES.MORE: {
              if (payment > leaseFilter.filterminimumnumber) {
                return true;
              }

              return false;
            }

            case LEASE_FILTER.TYPES.BETWEEN: {
              if (payment < leaseFilter.filtermaximumnumber
                  && payment > leaseFilter.filterminimumnumber) {
                return true;
              }

              return false;
            }

            default:
              return true;
          }
        });
      });
    }

    Object.getOwnPropertyNames(leaseOffers).forEach((property) => {
      if (leaseOffers[property].length === 0) {
        delete leaseOffers[property];
      }
    });

    return { offersByModel: sortOfferGroups(leaseOffers) };
  },
);

export const special = createSelector(
  [getOffers, getSelectedVehicle], (offers, selectedVehicle) => {
    const specialOffers = {};
    let allOffers = [];
    specialOffers[ALL_VEHICLES_FLAG] = [];

    blades.forEach((blade) => {
      specialOffers[blade.offersModelGroupName] = [];
    });

    specialTypes.forEach((type) => {
      const foundOffers = getOffersByType(offers, type, selectedVehicle);

      if (foundOffers) {
        Object.keys(foundOffers).forEach((key) => {
          if (key in specialOffers) {
            specialOffers[key] = specialOffers[key].concat(foundOffers[key]);
          } else {
            specialOffers[key] = foundOffers[key];
          }
        });
      }
    });

    const candidateOffers = offers.filter((offer) => specialTypes.indexOf(offer.SalesProgramType) !== -1);

    allOffers = filterByFlag(candidateOffers, ALL_VEHICLES_FLAG, ALL_VEHICLES_FLAG_VALUE);

    if (allOffers.length) {
      specialOffers[ALL_VEHICLES_FLAG] = specialOffers[ALL_VEHICLES_FLAG].concat(allOffers);
    }

    Object.getOwnPropertyNames(specialOffers).forEach((property) => {
      if (specialOffers[property].length === 0) {
        delete specialOffers[property];
      }
    });

    return { offersByModel: specialOffers };
  },
);

export const finance = createSelector(
  [getOffers, getSelectedVehicle, getStateDefaultSort],
  (offers, selectedVehicle, defaultSort) => {
    if (selectedVehicle.vehicle === undefined) {
      return { offersByModel: getOffersByType(offers, OFFERS.TYPES.FINANCE, selectedVehicle) };
    }

    return { offersByModel: getOffersByType(offers, OFFERS.TYPES.FINANCE, selectedVehicle, defaultSort) };
  },
);

export const getCurrentOffer = createSelector(
  [getOffers, getOfferDetails], (offers, offerDetails) => {
    const currentOffer = find(offers, { Id: offerDetails.offerId });

    return { currentOffer };
  },
);

export const getFormattedStartDate = createSelector(
  [getOffers, getOfferDetails], (offers, offerDetails) => {
    let openedOffer;

    if (offers.length > 0) {
      openedOffer = find(offers, { Id: offerDetails.offerId });
    }

    const formattedStartDate = openedOffer
      ? moment(openedOffer.StartDate).format('MMMM D, YYYY') : '';

    return { formattedStartDate };
  },
);

export const getFormattedEndDate = createSelector(
  [getOffers, getOfferDetails], (offers, offerDetails) => {
    let openedOffer;

    if (offers.length > 0) {
      openedOffer = find(offers, { Id: offerDetails.offerId });
    }

    const formattedEndDate = openedOffer
      ? moment(openedOffer.EndDate).format(DATE_FORMAT.DETAIL_RANGE) : '';

    return { formattedEndDate };
  },
);
